import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { Unit } from '@pixerito/api-client';
import { formatValue, monthLong, monthShort } from '../lib/format';

interface Props {
  periods: string[];
  values: (number | null)[];
  unit: Unit;
  label: string;
  height?: number;
}

const PAD = { top: 16, right: 16, bottom: 28, left: 52 };

function niceScale(min: number, max: number, ticks = 4) {
  if (min === max) {
    max = max === 0 ? 1 : max * 1.1;
    min = min * 0.9;
  }
  const range = max - min;
  const rough = range / ticks;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= rough) ?? rough;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const out: number[] = [];
  for (let v = lo; v <= hi + step / 2; v += step) out.push(Math.round(v * 100) / 100);
  return out;
}

function tickLabel(v: number, unit: Unit) {
  if (unit === 'percent') return `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
  if (unit === 'rating') return v.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  if (Math.abs(v) >= 10000) return `${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} mil`;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  return v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

/** Gráfico de linha da evolução mês a mês. Mês selecionado (o último) em destaque. */
export function LineChart({ periods, values, unit, label, height = 220 }: Props) {
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  const [hover, setHover] = useState<number | null>(null);
  const gradientId = useId().replace(/:/g, '');

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.max(280, Math.round(entry.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const geo = useMemo(() => {
    const present = values.filter((v): v is number => v !== null);
    const ticks = present.length ? niceScale(Math.min(...present), Math.max(...present)) : [0, 1];
    const yMin = ticks[0],
      yMax = ticks[ticks.length - 1];
    const innerW = width - PAD.left - PAD.right;
    const innerH = height - PAD.top - PAD.bottom;
    const x = (i: number) => PAD.left + (periods.length <= 1 ? innerW / 2 : (i * innerW) / (periods.length - 1));
    const y = (v: number) => PAD.top + innerH - ((v - yMin) / (yMax - yMin || 1)) * innerH;

    // Segmentos contínuos (um mês sem dado quebra a linha).
    const segments: { i: number; v: number }[][] = [];
    let cur: { i: number; v: number }[] = [];
    values.forEach((v, i) => {
      if (v === null) {
        if (cur.length) segments.push(cur);
        cur = [];
      } else cur.push({ i, v });
    });
    if (cur.length) segments.push(cur);

    const line = segments.map((s) => s.map((p, k) => `${k ? 'L' : 'M'}${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`).join('')).join('');
    const area = segments
      .filter((s) => s.length > 1)
      .map(
        (s) =>
          `M${x(s[0].i).toFixed(1)},${y(yMin).toFixed(1)}` +
          s.map((p) => `L${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`).join('') +
          `L${x(s[s.length - 1].i).toFixed(1)},${y(yMin).toFixed(1)}Z`,
      )
      .join('');

    const every = Math.max(1, Math.ceil(periods.length / Math.max(2, Math.floor(innerW / 56))));
    return { ticks, x, y, line, area, every, innerW };
  }, [values, periods, width, height]);

  const last = values.length - 1;
  const hovered = hover ?? last;

  function onMove(e: React.PointerEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * width;
    let best = 0,
      dist = Infinity;
    periods.forEach((_, i) => {
      const d = Math.abs(geo.x(i) - px);
      if (d < dist) {
        dist = d;
        best = i;
      }
    });
    setHover(best);
  }

  const hv = values[hovered];

  return (
    <div ref={wrap} style={{ width: '100%' }}>
      <div className="t-caption num" style={{ textAlign: 'right', minHeight: 16 }} aria-live="polite">
        <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{formatValue(hv, unit)}</span> · {monthLong(periods[hovered])}
      </div>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${label}: evolução de ${monthLong(periods[0])} a ${monthLong(periods[last])}`}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
        style={{ display: 'block', touchAction: 'pan-y' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--highlight)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--highlight)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {geo.ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={width - PAD.right} y1={geo.y(t)} y2={geo.y(t)} stroke="var(--faint)" strokeWidth="1" />
            <text
              x={PAD.left - 10}
              y={geo.y(t)}
              dy="0.35em"
              textAnchor="end"
              fontSize="11"
              fill="var(--ink-muted)"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {tickLabel(t, unit)}
            </text>
          </g>
        ))}
        {periods.map((p, i) =>
          (i % geo.every === 0 || i === last) && (i === last || last - i >= geo.every / 1.5) ? (
            <text
              key={p}
              x={geo.x(i)}
              y={height - 8}
              textAnchor={periods.length > 1 && i === last ? 'end' : i === 0 && periods.length > 1 ? 'start' : 'middle'}
              fontSize="11"
              fill={i === hovered ? 'var(--ink)' : 'var(--ink-muted)'}
              fontWeight={i === hovered ? 600 : 400}
            >
              {monthShort(p)}
            </text>
          ) : null,
        )}
        {geo.area && <path d={geo.area} fill={`url(#${gradientId})`} />}
        <line
          x1={geo.x(hovered)}
          x2={geo.x(hovered)}
          y1={PAD.top}
          y2={height - PAD.bottom}
          stroke="var(--border)"
          strokeWidth="1"
          strokeDasharray="3 3"
        />
        <path d={geo.line} fill="none" stroke="var(--highlight)" strokeWidth="2.25" strokeLinejoin="round" strokeLinecap="round" />
        {values.map((v, i) =>
          v === null ? null : (
            <rect
              key={i}
              x={geo.x(i) - (i === hovered ? 5 : 3)}
              y={geo.y(v) - (i === hovered ? 5 : 3)}
              width={i === hovered ? 10 : 6}
              height={i === hovered ? 10 : 6}
              rx={i === hovered ? 2.5 : 1.5}
              fill={i === hovered ? 'var(--highlight)' : 'var(--surface-raised)'}
              stroke="var(--highlight)"
              strokeWidth="1.5"
            />
          ),
        )}
      </svg>
    </div>
  );
}
